import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { includes } from 'lodash';
import { Observable, map, shareReplay, take } from 'rxjs';
import { distinctUntilChanged, filter, tap, withLatestFrom } from 'rxjs/operators';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { getWorkflowStepFromSchedule } from '@main-application/inspections/components/inspection-add-dialog/functions/get-workflow-step-from-schedule';
import { InspectionFormPostedResult } from '@main-application/inspections/components/inspection-add-dialog/inspection-add-regular-form/inspection-add-regular-form.component';
import { InspectionScheduleTypeListConst } from '@main-application/inspections/models/inspection-type-list.const';
import { InspectionScheduleType } from '@main-application/inspections/models/rest-inspections-model.interface';
import { InspectionService } from '@main-application/inspections/services/inspection.service';
import { setNewlyAddedInspection } from '@main-application/inspections/store/inspections.actions';
import { selectWorkflowStepsEnumeration } from '@main-application/store/selectors/enumeration.selectors';
import {
  selectTenantData,
  selectUserData,
  selectUserPermissionLevel,
} from '@main-application/store/selectors/user.selectors';
import { ButtonType } from '@shared/enums/button-type';
import { EIcon } from '@shared/enums/icon.enum';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { EnumerationItem } from '@shared/interfaces/enumeration-item';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { UserData } from '@shared/interfaces/user-data';
import { HelpRouterService } from '@shared/services/help-router.service';
import { valueChanges } from '@shared/utils/rxjs/value-changes.rxjs.util';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

export interface InspectionDialogData {
  inspectionId?: number;
  portfolioId?: number;
  propertyId?: number;
  diligenceId?: number;
  inspectionFormat?: InspectionScheduleType;
  redirectToList?: boolean;
}

export interface InspectionFormDefaults {
  inspectionTemplateId: number | null;
  endDate: Date | null;
  propertyId?: number | null;
  propertyIds?: number[] | null;
}

@UntilDestroy()
@Component({
  selector: 'app-inspection-add-dialog',
  templateUrl: './inspection-add-dialog.component.html',
  styleUrls: ['./inspection-add-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InspectionAddDialogComponent extends PuSubscribable implements OnInit {
  protected readonly ButtonType = ButtonType;
  protected readonly EIcon = EIcon;
  private userData?: UserData;
  userPermissions!: Set<string>;
  hasPermissionToAddStandard = true;
  hasPermissionToAddDynamic = true;
  hasPermissionToAddRepeating = true;
  hasPermissionToAddResident = true;
  formTypeInitialized = false;

  creating = false;
  inspectionTypeControl = new UntypedFormControl(InspectionScheduleType.Standard);
  inspectionTypeValue = null;
  saveAction = new EventEmitter<void>();
  formType$ = valueChanges(this.inspectionTypeControl).pipe(
    filter(() => {
      if (this.data?.diligenceId && this.formTypeInitialized) return;
      return true;
    }),
    map(type => this.getInspectionFormType(type)),
    tap(() => {
      setTimeout(() => {
        this.formTypeInitialized = true;
      }, 300);
    }),
    distinctUntilChanged(),
    shareReplay()
  );
  formDefaults: InspectionFormDefaults = {
    inspectionTemplateId: null,
    endDate: null,
    propertyId: null,
  };

  inspectionScheduleTypeList$ = this.getWorkflowStepEnumeration().pipe(
    withLatestFrom(this.store.select(selectTenantData)),
    map(([workflowSteps, settings]) => {
      let scheduleTypeList = InspectionScheduleTypeListConst;
      if (this.data?.diligenceId)
        scheduleTypeList = scheduleTypeList.map(el => {
          if (el.value === InspectionScheduleType.Standard) return { ...el, label: 'Diligence' };
          return el;
        });

      const getMappedOptions = scheduleTypeList.map(i => {
        const data: IRadioButtonOption<any> = {
          ...i,
        };
        const step = getWorkflowStepFromSchedule(i.value);
        const workflowStep = workflowSteps.find(s => s.enumerationValue === step);
        if (workflowStep) {
          const stepName = workflowStep.displayText.toUpperCase();
          data.label = `> ${stepName}`;
          data.displayValue = `Dynamic: ${stepName}`;
        }
        return data;
      });

      return getMappedOptions;
    })
  );

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private helpRouterService: HelpRouterService,
    private store: Store<{}>,
    public dialogRef: MatDialogRef<InspectionAddDialogComponent>,
    @Inject(MAT_DIALOG_DATA) protected data: InspectionDialogData,
    private inspectionService: InspectionService,
    private snackbarService: SnackbarService
  ) {
    super();
    this.data = data || {};
    this.store
      .select(selectUserPermissionLevel)
      .pipe(
        take(1),
        filter(permissionLevel => !!permissionLevel?.length)
      )
      .subscribe(permissios => {
        this.userPermissions = new Set(permissios);
        const allPermissions = PermissionLevelType.AllPermissions;
        const dynamicInspectionsAdd = PermissionLevelType.DynamicInspections_Edit;
        const repeatingInspectionsAdd = PermissionLevelType.RepeatingInspections_Edit;
        const residentInspectionsAdd = PermissionLevelType.ResidentInspections_List;
        const standardInspectionAdd = PermissionLevelType.Inspections_List;

        this.hasPermissionToAddDynamic =
          this.userPermissions?.has(allPermissions) || this.userPermissions?.has(dynamicInspectionsAdd);
        this.hasPermissionToAddRepeating =
          this.userPermissions?.has(allPermissions) || this.userPermissions?.has(repeatingInspectionsAdd);
        this.hasPermissionToAddResident =
          this.userPermissions?.has(allPermissions) || this.userPermissions?.has(residentInspectionsAdd);
        this.hasPermissionToAddStandard =
          this.userPermissions?.has(allPermissions) || this.userPermissions?.has(standardInspectionAdd);
      })
      .untilDestroyed(this);
  }

  ngOnInit(): void {
    const inspectionType = this.onGetInspectionType(this.data.inspectionFormat || InspectionScheduleType.Standard);
    this.data.inspectionFormat = inspectionType;
    this.inspectionTypeControl.setValue(inspectionType);
    if (this.data?.diligenceId) this.inspectionTypeValue = inspectionType;

    this.store
      .select(selectUserData)
      .pipe(
        untilDestroyed(this),
        filter((userData: UserData) => !!userData),
        tap((userData: UserData) => {
          this.userData = userData;
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.inspectionTypeControl.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(() => {
          if (this.data?.diligenceId) {
            this.snackbarService.warning('Inspection type cannot be changed once diligence.');
            this.inspectionTypeControl.setValue(this.inspectionTypeValue, { emitEvent: false });
            return;
          }
          this.checkTurnsPackage();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.checkTurnsPackage();
  }

  private checkTurnsPackage() {
    const { value } = this.inspectionTypeControl;
    if (![InspectionScheduleType.Standard, InspectionScheduleType.Repeating].includes(value)) {
      // TODO?
    }
    this.cdr.detectChanges();
  }

  cancel() {
    this.dialogRef.close();
  }

  save() {
    this.creating = true;
    this.saveAction.emit();
  }

  formPosted(data: InspectionFormPostedResult) {
    if (!data.result) {
      this.creating = false;
      this.cdr.detectChanges();
      return;
    }

    const queryParams = this.helpRouterService.getGlobalPreservedParams();
    const isDynamic = includes(
      [
        InspectionScheduleType.DynamicNotice,
        InspectionScheduleType.DynamicMoveIn,
        InspectionScheduleType.DynamicMoveOut,
        InspectionScheduleType.DynamicMakeReady,
      ],
      this.inspectionTypeControl.value
    );

    const isStandart = this.inspectionTypeControl.value === InspectionScheduleType.Standard;
    const isResindent = this.inspectionTypeControl.value === InspectionScheduleType.Resident;
    const isRepeating = this.inspectionTypeControl.value === InspectionScheduleType.Repeating;

    const handleResult = () => {
      this.dialogRef.close(true);
      this.store.dispatch(
        setNewlyAddedInspection({ inspection: data.inspection, inspectionType: this.inspectionTypeControl.value })
      );
    };
    const isBulk = data.inspection?.['bulkUnitIds']?.length > 1 || data.inspection?.['bulkUnitIds'] === null;
    if (this.data.diligenceId) {
      if (isBulk) {
        this.inspectionService.fetchOneTimeInspectionList$.next();
      } else {
        handleResult();
      }

      this.dialogRef.close(true);
      return;
    }

    if (this.data.redirectToList === false) {
      this.dialogRef.close(true);
      return;
    }

    if (isStandart && isBulk) {
      this.inspectionService.fetchOneTimeInspectionList$.next();
      return this.router.navigate(['inspections/bulk'], { queryParams }).then(handleResult);
    } else if (isStandart || isResindent) {
      this.inspectionService.fetchOneTimeInspectionList$.next();
    }

    if (isStandart) this.router.navigate(['inspections/user'], { queryParams }).then(handleResult);
    if (isResindent) this.router.navigate(['inspections/resident'], { queryParams }).then(handleResult);
    if (isRepeating) this.router.navigate(['inspections/repeating'], { queryParams }).then(handleResult);
    if (isDynamic) this.router.navigate(['inspections/dynamic'], { queryParams }).then(handleResult);
  }

  closeModal(): void {
    this.dialogRef.close(false);
  }

  inspectionTypeChanged($event: InspectionScheduleType) {
    this.inspectionTypeControl.setValue($event);
    this.cdr.detectChanges();
  }

  private getInspectionFormType(type: InspectionScheduleType): 'dynamic' | 'regular' | 'repeating' | 'resident' {
    const inspectionType = this.onGetInspectionType(this.data.inspectionFormat || InspectionScheduleType.Standard);
    if (!this.hasPermissionToAddRepeating && type === InspectionScheduleType.Repeating) {
      this.snackbarService.error(`${PermissionLevelType.RepeatingInspections_Edit} permission required`);
      this.inspectionTypeControl.setValue(inspectionType, { emitEvent: false });
      return this.getInspectionTypeStr(inspectionType);
    }
    if (!this.hasPermissionToAddStandard && type === InspectionScheduleType.Standard) {
      this.snackbarService.error(`${PermissionLevelType.Inspections_List} permission required`);
      this.inspectionTypeControl.setValue(inspectionType, { emitEvent: false });
      return this.getInspectionTypeStr(inspectionType);
    }
    if (!this.hasPermissionToAddResident && type === InspectionScheduleType.Resident) {
      this.snackbarService.error(`${PermissionLevelType.ResidentInspections_List} permission required`);
      this.inspectionTypeControl.setValue(inspectionType, { emitEvent: false });
      return this.getInspectionTypeStr(inspectionType);
    }
    if (
      !this.hasPermissionToAddDynamic &&
      (type === InspectionScheduleType.DynamicNotice ||
        type === InspectionScheduleType.DynamicMoveIn ||
        type === InspectionScheduleType.DynamicMoveOut ||
        type === InspectionScheduleType.DynamicMakeReady)
    ) {
      this.inspectionTypeControl.setValue(inspectionType, { emitEvent: false });
      this.snackbarService.error(`${PermissionLevelType.DynamicInspections_Edit} permission required`);
      return this.getInspectionTypeStr(inspectionType);
    }

    if (
      [
        InspectionScheduleType.DynamicNotice,
        InspectionScheduleType.DynamicMoveIn,
        InspectionScheduleType.DynamicMoveOut,
        InspectionScheduleType.DynamicMakeReady,
      ].includes(type)
    ) {
      return 'dynamic';
    } else if (type === InspectionScheduleType.Repeating) {
      return 'repeating';
    } else if (type === InspectionScheduleType.Resident) {
      return 'regular';
    } else if (type === InspectionScheduleType.Standard) {
      return 'regular';
    }
  }

  formChanged($event: InspectionFormDefaults) {
    this.formDefaults = $event;
  }

  private getWorkflowStepEnumeration(): Observable<EnumerationItem[]> {
    return this.store.select(selectWorkflowStepsEnumeration);
  }

  private onGetInspectionType(inspectionType: InspectionScheduleType): InspectionScheduleType {
    if (!this.hasPermissionToAddStandard && inspectionType === InspectionScheduleType.Standard) {
      return this.onGetInspectionType(InspectionScheduleType.Resident);
    }
    if (!this.hasPermissionToAddResident && inspectionType === InspectionScheduleType.Resident) {
      return this.onGetInspectionType(InspectionScheduleType.Repeating);
    }
    if (!this.hasPermissionToAddRepeating && inspectionType === InspectionScheduleType.Repeating) {
      return this.onGetInspectionType(InspectionScheduleType.DynamicNotice);
    }
    if (
      !this.hasPermissionToAddDynamic &&
      (inspectionType === InspectionScheduleType.DynamicNotice ||
        inspectionType === InspectionScheduleType.DynamicMoveIn ||
        inspectionType === InspectionScheduleType.DynamicMoveOut ||
        inspectionType === InspectionScheduleType.DynamicMakeReady)
    ) {
      return this.onGetInspectionType(InspectionScheduleType.Standard);
    }

    return inspectionType;
  }

  private getInspectionTypeStr(inspectionType: InspectionScheduleType): 'dynamic' | 'regular' {
    return inspectionType === InspectionScheduleType.DynamicNotice ||
      inspectionType === InspectionScheduleType.DynamicMoveIn ||
      inspectionType === InspectionScheduleType.DynamicMoveOut ||
      inspectionType === InspectionScheduleType.DynamicMakeReady
      ? 'dynamic'
      : 'regular';
  }
}
